import createError from "@/clir/utils/createError";

/* Constantes de valorisation des paramètres : */
const exportFormats = { CSV: "CSV", PDF: "PDF" };
const datasTypes = { STRING: "string", BOOLEAN: "boolean", NUMBER: "number", DATE: "date", ARRAY: "array", ANY: "object" };
const orientations = { LANDSCAPE: "landscape", PORTRAIT: "portait" };
const alignments = { START: "start", CENTER: "center", END: "end", JUSTIFIED: "justified" };

/* clés racines de l'objet de paramètres : */
const paramsKeys = {
    data_uri: { required: true, type: datasTypes.STRING },
    body_uri: { required: false, type: datasTypes.ANY },
    format: { required: false, authorizedValues: Object.values(exportFormats), defaultValue: exportFormats.PDF, type: datasTypes.STRING },
    title: { required: false, defaultValue: "Export PDF", type: datasTypes.STRING },
    orientation: { required: false, authorizedValues: Object.values(orientations), defaultValue: orientations.LANDSCAPE, type: datasTypes.STRING, skipWhenFormat: exportFormats.CSV },
    width: { required: false, defaultValue: 842, type: datasTypes.NUMBER, skipWhenFormat: exportFormats.CSV },
    height: { required: false, defaultValue: 595, type: datasTypes.NUMBER, skipWhenFormat: exportFormats.CSV },
    expanded: { required: false, authorizedValues: [true, false], defaultValue: true, type: datasTypes.BOOLEAN, skipWhenFormat: exportFormats.CSV },
    fields: { required: false, type: datasTypes.ARRAY }
};

/* clés des champs : */
const fieldsKeys = {
    "key": { required: true, type: datasTypes.STRING },
    label: { required: false, type: datasTypes.STRING },
    type: { required: false, authorizedValues: [datasTypes.STRING, datasTypes.NUMBER, datasTypes.DATE, datasTypes.BOOLEAN], defaultValue: datasTypes.STRING, type: datasTypes.STRING },
    width: { required: false, type: datasTypes.NUMBER},
    alignment: { required: false, authorizedValues: Object.values(alignments), defaultValue: alignments.START, type: datasTypes.STRING, skipWhenFormat: exportFormats.CSV },
    format: { required: false, type: datasTypes.STRING }
};

const exportLog = [];

/* objet complété avec les valeurs par défaut si nécessaire : */
const completedParamsObject = (paramsList, paramsObject) => {
    const params = { ...paramsObject };
    Object.keys(paramsList).forEach(objKey => {
        if (typeof paramsList[objKey].defaultValue != "undefined" && typeof params[objKey] == "undefined")
            params[objKey] = paramsList[objKey].defaultValue;
    });
    return params;
};

/* vérification de la présence d'un paramètre : 
    param : clé du paramètre (exemple : "data_uri")
    paramsList : Contraintes du param (exemple : paramsKeys)
    paramsObject : objet de paramètres testé
*/
const isMissingParam = (param, paramsList, paramsObject) => {
    // présence si required :
    const isMissing = paramsList[param].required && typeof paramsObject[param] == "undefined";
    if (isMissing) exportLog.push(`${param} : absent`);
    return isMissing;
};
/* vérification de la validité de type d'un paramètre : 
    param : clé du paramètre (exemple : "DATA_URI")
    paramsList : Contraintes du param (exemple : paramsKeys)
    paramsObject : objet de paramètres testé
*/
const isValidParamType = (param, paramsList, paramsObject) => {
    // type valide: 
    const type = paramsList[param].type.toLowerCase();
    const value = paramsObject[param];
    let isValid = true;
    // on a vérifié sa présence et son required dans isMissingParam 
    // donc si il est undefined son type est valide :
    if (typeof value == "undefined") isValid = true;
    // contrôle particulier pour les tableaux :
    else if (type == datasTypes.ARRAY) {
        if (!Array.isArray(value)) isValid = false;
    }
    // contrôle classique :
    else if (typeof value !== type) isValid = false;
    if (!isValid)
        exportLog.push(`${param} : type invalide`);
    return isValid;
};

/* vérification de la validité d'un paramètre : 
    param : clé du paramètre (exemple : "DATA_URI")
    paramsList : Contraintes du param (exemple : paramsKeys)
    paramsObject : objet de paramètres testé
*/
const isValidParam = (param, paramsList, paramsObject) => {
    // type valide: 
    const authorizedValues = paramsList[param].authorizedValues;
    const value = paramsObject[param];
    let isValid = true;
    if (typeof authorizedValues != "undefined" && authorizedValues.length && !authorizedValues.includes(value)) isValid = false;
    if (!isValid) exportLog.push(`${param} : valeur invalide`)
    else exportLog.push(`${param} : OK`);
    return isValid;
};

/* vérification de la validité d'un objet de paramètres :  */
const isValidParamsObject = (paramsList, paramsObject) => {
    let isValid = true;
    Object.keys(paramsList).forEach(objKey => {
        if (isMissingParam(objKey, paramsList, paramsObject)) isValid = false;
        else if (!isValidParamType(objKey, paramsList, paramsObject)) isValid = false;
        else if (!isValidParam(objKey, paramsList, paramsObject)) isValid = false;
    });
    return isValid;
};

/* formatage d'un objet pour un type d'export:  */
const prepareForExport = (paramsList, paramsObject, format) => {
    Object.keys(paramsObject).forEach(objKey => {
        if (typeof paramsList[objKey] == "undefined" || paramsList[objKey].skipWhenFormat == format) delete paramsObject[objKey];
    });
    return paramsObject;
};

/* calcul automatique des largeurs de colonnes : */
const getColWidth = (field, paramsObject) => {
    const docWidth = 100;
    let nbFreeCols = 0;
    let usedWidth = 0;
    paramsObject.fields.forEach(paramsField => {
        if (typeof paramsField.width == datasTypes.NUMBER) {
            usedWidth += paramsField.width || 0;
        } else {
            nbFreeCols++;
        }
    });
    const colWidth = Math.max((docWidth - usedWidth) / nbFreeCols, 0);
    return colWidth;
};

/*******************************************************/
/*******************************************************/
/* fonction principale de traitement des paramètres :  */
/*******************************************************/
/*******************************************************/
const validateParamsForExport = (paramsObject) => {
    // -------------
    // clés racine :
    // -------------
    // valorisation avec les valeurs par défaut des clés manquantes :
    let params = completedParamsObject(paramsKeys, paramsObject);
    // contrôles de validité :
    exportLog.push(`------------------------------------`);
    exportLog.push(`clé racine :`);
    exportLog.push(`------------------------------------`);
    if (!isValidParamsObject(paramsKeys, params)) {
        logError();
    }
    // formatage pour le type d'export :
    params = prepareForExport(paramsKeys, params, params.format);
    // -----------------
    // clés des champs :
    // -----------------
    let fieldsErrorsCount = 0;
    exportLog.push(`------------------------------------`);
    exportLog.push(`clés des champs : `);
    exportLog.push(`------------------------------------`);
    params.fields = params.fields.map(fieldParams => {
        // valorisation avec les valeurs par défaut des clés manquantes :
        let field = completedParamsObject(fieldsKeys, fieldParams);
        // contrôles de validité :
        exportLog.push(`---  ${field.key}  ---`);
        if (!isValidParamsObject(fieldsKeys, field)) {
            fieldsErrorsCount++;
        }
        // formatage pour le type d'export :
        if (!fieldsErrorsCount) field = prepareForExport(fieldsKeys, field, params.format);
        // calcul des largeurs de colonnes non valorisées :
        if (typeof field.width == "undefined") field.width = getColWidth(field, params);
        // transposition des Booleens en String :
        if (field.type == datasTypes.BOOLEAN) field.type = datasTypes.STRING;
        return field;
    });
    if (fieldsErrorsCount) logError();
    return params;
};

const logError = () => {
    throw createError({
        status: "Paramètres incorrects",
        statusText: exportLog.join(`
`)
    });
}
const baseURL = `/${process.env.VUE_APP_APPLICATION_NAME}/api`;
/**
 * Fonction utilitaire d'export de données au format PDF ou CSV
 *
 *
 * @author <span><span class="fw-bold">Pascal Ouzeau</span><a class="ms-2" href="mailto:pascal.ouzeau@dgfip.finances.gouv.fr">pascal.ouzeau@dgfip.finances.gouv.fr</a></span>
 * @since Version 1.2.1
 * @version 1.2.1
 * @displayName exportDatas
 */
export default {
    methods: {
        async exportDatas(paramsObject) {
            /**
             * déclenché pour demander la récupération d'enregistrements
             *
             * @property {object} paramsObject statut actuel du tableau
             */
            try {
                const params = validateParamsForExport(paramsObject);
                const payload = {
                    url: `${baseURL}/export`,
                    options: {
                        method: "POST",
                        cors: true,
                        headers: [
                            ["X-XSRF-Token", this.getTokenCsrf],
                            ["Content-Type", "application/json"]
                        ],
                        body: JSON.stringify(params)
                    }
                };
                const res = await this.DGFiPFetch(payload);
                const blob = await res.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = `${params.title}.${params.format}`;
                document.body.appendChild(a);
                a.click();
                a.remove();
            } catch (e) {
                this.pushError(e);
            }
        }
    }
}
